/**
 * embedAuth v3 — Persistencia de sesión robusta cross-iframe / cross-tab / cross-subdomain
 *
 * 5 capas en orden de confiabilidad para iframes embebidos en dominios externos:
 *
 *   1) localStorage (okbot.okgo.mx) — síncrono, compartido entre iframes del mismo
 *      origen. Lectura instantánea para el primer render de React.
 *
 *   2) IndexedDB (okbot-db) — más resiliente que localStorage ante Safari ITP /
 *      Firefox ETP en contextos de iframe 3rd-party. Async; se usa como respaldo
 *      y como escritura secundaria.
 *
 *   3) Cookie Domain=.okgo.mx — atraviesa TODOS los subdominios (app.okgo.mx,
 *      okbot.okgo.mx, etc.). El backend también la escribe con Set-Cookie en
 *      cada login para que sobreviva a borrados de localStorage.
 *
 *   4) BroadcastChannel("okbot-auth") — propaga login/logout en tiempo real entre
 *      pestañas e iframes del mismo origen sin necesidad de recargar.
 *
 *   5) GET /api/auth/session (credentials:include) — cuando todas las capas de
 *      almacenamiento local están vacías, el navegador envía la cookie al backend
 *      y éste devuelve el usuario hidratado. Funciona incluso si localStorage e
 *      IndexedDB están bloqueados.
 */

import { useEffect, useState } from "react";

const SESSION_KEY  = "okbot_session";
const LEGACY_USER  = "okbot_user";
const LEGACY_TOKEN = "okbot_admin_token";
const TTL_MS       = 30 * 24 * 60 * 60 * 1000; // 30 días
const COOKIE_NAME  = "okbot_token";
const COOKIE_DOMAIN = ".okgo.mx";

/* ─── helpers ──────────────────────────────────────────────────────────────── */

function nowTs() { return Date.now(); }

function expired(session) {
  if (!session) return true;
  const ts = session.ts || 0;
  return (nowTs() - ts) > TTL_MS;
}

/* ─── BroadcastChannel ──────────────────────────────────────────────────────── */

let _channel = null;
function getChannel() {
  if (_channel) return _channel;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      _channel = new BroadcastChannel("okbot-auth");
    }
  } catch (_) {}
  return _channel;
}

/* ─── Capa 1: localStorage ─────────────────────────────────────────────────── */

function lsRead() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (!expired(s)) return s;
    }
  } catch (_) {}
  // Migración desde claves legacy
  try {
    const u = localStorage.getItem(LEGACY_USER);
    if (u) {
      const user  = JSON.parse(u);
      const token = localStorage.getItem(LEGACY_TOKEN) || null;
      const s = { user, token, ts: nowTs(), source: "migrated" };
      try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (_) {}
      return s;
    }
  } catch (_) {}
  return null;
}

function lsWrite(session) {
  try {
    if (session) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      if (session.user)  localStorage.setItem(LEGACY_USER,  JSON.stringify(session.user));
      if (session.token) localStorage.setItem(LEGACY_TOKEN, session.token);
    } else {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(LEGACY_USER);
      localStorage.removeItem(LEGACY_TOKEN);
    }
  } catch (_) {}
}

/* ─── Capa 2: IndexedDB ────────────────────────────────────────────────────── */

const IDB_NAME  = "okbot-db";
const IDB_STORE = "session";
const IDB_KEY   = "main";

function _openIDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no-idb"));
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = (e) => {
      try { e.target.result.createObjectStore(IDB_STORE); } catch (_) {}
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbRead() {
  try {
    const db = await _openIDB();
    return new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => {
        const s = req.result || null;
        resolve(s && !expired(s) ? s : null);
      };
      req.onerror = () => resolve(null);
    });
  } catch (_) { return null; }
}

async function idbWrite(session) {
  try {
    const db = await _openIDB();
    return new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      if (session) store.put(session, IDB_KEY);
      else         store.delete(IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror    = () => resolve();
    });
  } catch (_) {}
}

/* ─── Capa 3: Cookie (Domain=.okgo.mx) ────────────────────────────────────── */

function cookieRead() {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) { return null; }
}

function cookieWrite(token) {
  try {
    if (token) {
      const maxAge = Math.floor(TTL_MS / 1000);
      // Domain=.okgo.mx → atraviesa app.okgo.mx, okbot.okgo.mx, etc.
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; Max-Age=${maxAge}; Path=/; Domain=${COOKIE_DOMAIN}; Secure; SameSite=None`;
    } else {
      document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; Domain=${COOKIE_DOMAIN}; Secure; SameSite=None`;
      // Limpiar también sin dominio explícito (por si fue escrita así antes)
      document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; Secure; SameSite=None`;
    }
  } catch (_) {}
}

/* ─── Capa 5: Hidratación desde backend ────────────────────────────────────── */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
let _hydratePromise = null; // de-duplicate concurrent calls

export async function hydrateFromBackend() {
  if (_hydratePromise) return _hydratePromise;
  _hydratePromise = (async () => {
    try {
      const token = cookieRead() || (lsRead() || {}).token;
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      let res;
      try {
        res = await fetch(`${API_BASE}/auth/session`, {
          method: "GET",
          credentials: "include",    // envía cookie automáticamente
          headers,
        });
      } catch (_networkErr) {
        // Error de red (offline, CORS, etc.) → conservar sesión local
        return undefined;
      }

      // 401/403: token inválido → indicar al caller que limpie la sesión local
      if (res.status === 401 || res.status === 403) return null;
      if (!res.ok) return undefined; // otro error HTTP → conservar sesión

      const data = await res.json();
      if (data?.valid && data.user) {
        const session = { user: data.user, token: data.token || token || null, ts: nowTs(), source: "backend" };
        lsWrite(session);
        cookieWrite(session.token);
        await idbWrite(session);
        // Notificar a otros contextos
        const ch = getChannel();
        try { ch?.postMessage({ type: "login", session }); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent("okbot:auth-change", { detail: session })); } catch (_) {}
        return data.user;
      }
      return null;
    } catch (_) {
      return undefined; // error inesperado → conservar sesión local
    } finally {
      // Resetear después de 10s para permitir re-intentos futuros
      setTimeout(() => { _hydratePromise = null; }, 10_000);
    }
  })();
  return _hydratePromise;
}

/* ─── Escritura unificada (todas las capas) ─────────────────────────────────── */

function _broadcast(session) {
  const ch = getChannel();
  try { ch?.postMessage(session ? { type: "login", session } : { type: "logout" }); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent("okbot:auth-change", { detail: session || null })); } catch (_) {}
}

/* ─── API pública ───────────────────────────────────────────────────────────── */

export function getSession() { return lsRead(); }

export function getUser() {
  const s = lsRead();
  return s ? s.user || null : null;
}

export function getToken() {
  const s = lsRead();
  if (s?.token) return s.token;
  return cookieRead();
}

export function setSession({ user, token }) {
  const session = { user: user || null, token: token || null, ts: nowTs() };
  lsWrite(session);
  cookieWrite(token);
  idbWrite(session); // async, fire-and-forget
  _broadcast(session);
  return session;
}

export function clearSession() {
  lsWrite(null);
  cookieWrite(null);
  idbWrite(null); // async, fire-and-forget
  _broadcast(null);
}

/**
 * subscribe(cb) — se llama cuando la sesión cambia en cualquier
 * iframe/pestaña del mismo origen. Devuelve función para des-suscribir.
 */
export function subscribe(cb) {
  const ch = getChannel();
  const onMsg = (ev) => {
    if (!ev?.data) return;
    if (ev.data.type === "logout") cb(null);
    else if (ev.data.type === "login") cb(ev.data.session?.user || null);
  };
  if (ch) ch.addEventListener("message", onMsg);

  const onStorage = (ev) => {
    if (ev.key !== SESSION_KEY && ev.key !== LEGACY_USER) return;
    cb(getUser());
  };
  window.addEventListener("storage", onStorage);

  const onCustom = (ev) => cb(ev.detail?.user || null);
  window.addEventListener("okbot:auth-change", onCustom);

  return () => {
    if (ch) ch.removeEventListener("message", onMsg);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("okbot:auth-change", onCustom);
  };
}

/**
 * useEmbedAuth() — hook React que mantiene el estado del usuario sincronizado
 * entre todos los iframes/pestañas/subdominios del mismo grupo.
 *
 * Al montar: intenta hidratar desde IndexedDB y, si todo falla, desde backend.
 */
export function useEmbedAuth() {
  const [user, setUser] = useState(() => getUser());

  useEffect(() => {
    // Suscribir a cambios en tiempo real
    const unsub = subscribe((u) => setUser(u));

    const localUser = getUser();

    if (!localUser) {
      // Sin sesión local: intentar recuperar desde capas de respaldo
      idbRead().then((s) => {
        if (s?.user) {
          lsWrite(s);
          setUser(s.user);
          _broadcast(s);
          // Validar en background que el token siga siendo válido
          hydrateFromBackend().then((u) => { if (u) setUser(u); });
        } else {
          hydrateFromBackend().then((u) => { if (u) setUser(u); });
        }
      });
    } else {
      // Hay sesión local: validar con el backend en background para:
      //   1) Detectar tokens expirados o revocados.
      //   2) Recibir el token renovado si el backend lo extendió (< 7 días).
      //   3) Mantener el ts fresco para que no expire en localStorage (30 días).
      hydrateFromBackend().then((u) => {
        if (u) {
          setUser(u);
        } else if (u === null) {
          // Backend rechazó el token (expirado/revocado) → limpiar sesión local
          clearSession();
          setUser(null);
        }
        // u === undefined significa error de red — mantener sesión local
      });
    }

    // Revalidar silenciosamente cuando el usuario vuelve a la pestaña.
    // NUNCA borrar la sesión aquí — solo actualizar si el backend devuelve usuario.
    // Esto previene "cierres de sesión" cuando el navegador pausa/reanuda la pestaña.
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // Reset el deduplicador para forzar una nueva llamada
      _hydratePromise = null;
      hydrateFromBackend().then((u) => {
        if (u) setUser(u);
        // null o undefined → no tocar la sesión local
      });
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      unsub();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    user,
    token: getToken(),
    login : ({ user, token }) => { setSession({ user, token }); setUser(user); },
    logout: () => { clearSession(); setUser(null); },
  };
}
